import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, CircularProgress, Tooltip, Alert, Stack,
  Divider, Paper, InputAdornment,
} from '@mui/material';
import {
  Add, Edit, Delete, Close, Category as CategoryIcon, Save, Check, Label,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

const swalTop = { customClass: { container: 'swal-on-top' } };
function headers() { return { Authorization: `Bearer ${localStorage.getItem('church_token')}` }; }

const PALETTE = [
  '#0B2545', '#13315C', '#1565C0', '#0F766E', '#C89211',
  '#E65100', '#B91C1C', '#6a1b9a', '#00838F', '#37474F', '#795548', '#64748B',
];

const EMPTY = { id: null, name: '', code_prefix: '', color: PALETTE[0], status: 'active' };

export default function CategoryManagerDialog({
  open, onClose, title,
  listURL, addURL, updateURL, deleteURL, onChanged,
}) {
  const [items, setItems]   = useState([]);
  const [loading, setLoad]  = useState(false);
  const [form, setForm]     = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const load = () => {
    setLoad(true);
    axios.get(listURL, { headers: headers() })
      .then(r => { if (r.data.success) setItems(r.data.data); })
      .finally(() => setLoad(false));
  };

  useEffect(() => { if (open) { load(); setForm(EMPTY); setError(''); } }, [open]);

  const editing = form.id !== null;
  const resetForm = () => setForm(EMPTY);

  const handleSave = async () => {
    setError('');
    if (!form.name.trim() || !form.code_prefix.trim()) {
      setError('Name and code prefix are both required.');
      return;
    }
    setSaving(true);
    try {
      const url = editing ? updateURL : addURL;
      const payload = {
        ...(editing ? { id: form.id } : {}),
        name: form.name.trim().toUpperCase(),
        code_prefix: form.code_prefix.trim().toUpperCase(),
        color: form.color,
        status: form.status,
      };
      const r = await axios.post(url, payload, { headers: headers() });
      if (!r.data.success) throw new Error(r.data.message || 'Could not save.');
      resetForm();
      load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (c) => {
    Swal.fire({
      ...swalTop,
      title: `Delete "${c.name}"?`,
      text: 'This will only work if no types are still using this category.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#B91C1C',
      confirmButtonText: 'Delete',
    }).then(async r => {
      if (!r.isConfirmed) return;
      try {
        const resp = await axios.post(deleteURL, { id: c.id }, { headers: headers() });
        if (!resp.data.success) throw new Error(resp.data.message);
        load();
        onChanged?.();
      } catch (err) {
        Swal.fire({ ...swalTop, icon: 'error', title: 'Cannot delete', text: err.response?.data?.message || err.message });
      }
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        background: 'linear-gradient(90deg, #0B2545, #13315C)', color: '#fff', py: 2.5,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5, bgcolor: 'secondary.main',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CategoryIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>{title}</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Add, rename or remove the groupings used to organise types.
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Grid container>
          <Grid item xs={12} md={5} sx={{ p: 3, borderRight: { md: '1px solid #E2E8F0' }, bgcolor: '#FAFBFC' }}>
            <Typography variant="overline" fontWeight={700} color="primary.main" sx={{ letterSpacing: '0.14em' }}>
              {editing ? 'Edit Category' : 'New Category'}
            </Typography>

            {error && <Alert severity="error" sx={{ my: 1.5, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Stack spacing={2} mt={1.5}>
              <TextField
                fullWidth size="small" label="Category Name" required
                placeholder="e.g. UTILITIES"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value.toUpperCase() })}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Label sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
                }}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
              <TextField
                fullWidth size="small" label="Code Prefix" required
                placeholder="e.g. UTL"
                helperText="2–4 letters used to auto-generate codes (e.g. UTL-001)"
                value={form.code_prefix}
                onChange={e => setForm({ ...form, code_prefix: e.target.value.toUpperCase().slice(0, 5) })}
                inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'monospace' } }}
              />

              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 10 }}>
                  Accent Colour
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.75} mt={1}>
                  {PALETTE.map(c => (
                    <Box key={c} onClick={() => setForm({ ...form, color: c })}
                      sx={{
                        width: 26, height: 26, borderRadius: '50%', cursor: 'pointer',
                        bgcolor: c,
                        border: form.color === c ? '3px solid #fff' : '3px solid transparent',
                        boxShadow: form.color === c ? `0 0 0 2px ${c}` : '0 0 0 1px #CBD5E1',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'transform 120ms',
                        '&:hover': { transform: 'scale(1.1)' },
                      }}>
                      {form.color === c && <Check sx={{ color: '#fff', fontSize: 14 }} />}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Stack direction="row" spacing={1} pt={1}>
                {editing && (
                  <Button variant="outlined" onClick={resetForm} fullWidth>Cancel</Button>
                )}
                <Button
                  variant="contained" onClick={handleSave} disabled={saving} fullWidth
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : (editing ? <Save /> : <Add />)}
                >
                  {editing ? 'Save Changes' : 'Add Category'}
                </Button>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} md={7} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography variant="overline" fontWeight={700} color="primary.main" sx={{ letterSpacing: '0.14em' }}>
                Existing Categories
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {items.length} total
              </Typography>
            </Box>
            <Divider sx={{ mb: 1.5 }} />

            {loading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={26} /></Box>
            ) : items.length === 0 ? (
              <Box textAlign="center" py={4} color="text.secondary">
                <CategoryIcon sx={{ fontSize: 40, color: '#CBD5E1', mb: 1 }} />
                <Typography variant="body2">No categories yet. Add one on the left.</Typography>
              </Box>
            ) : (
              <Stack spacing={1} sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
                {items.map(c => (
                  <Paper key={c.id} variant="outlined"
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      p: 1.5, borderColor: '#E2E8F0',
                      bgcolor: form.id === c.id ? 'rgba(11,37,69,0.03)' : '#fff',
                    }}>
                    <Box sx={{ width: 10, height: 34, borderRadius: 0.75, bgcolor: c.color || '#64748B', flexShrink: 0 }} />
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={600} noWrap>{c.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {c.code_prefix}-###
                      </Typography>
                    </Box>
                    <Chip
                      size="small" label={c.status}
                      color={c.status === 'active' ? 'success' : 'default'}
                      variant={c.status === 'active' ? 'filled' : 'outlined'}
                      sx={{ textTransform: 'capitalize' }}
                    />
                    <Tooltip title="Edit">
                      <IconButton size="small"
                        onClick={() => setForm({
                          id: c.id, name: c.name, code_prefix: c.code_prefix,
                          color: c.color || PALETTE[0], status: c.status,
                        })}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(c)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                ))}
              </Stack>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: '#FAFBFC' }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
